import { oauthAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "./logo.png";

export const crowdStrike = defineConnector({
  identifier: "crowdStrike",
  name: "CrowdStrike",
  tagLine: "Endpoint security platform",
  keywords:
    "falcon edr xdr endpoint detection and response antivirus incidents hosts",
  logo,
  protocol: "http",
  tags: ["featured", "security"],
  defaultUrlPattern: () =>
    "https://api\\.([a-z0-9-]+\\.)?crowdstrike\\.(com|mil)/.*",
  auth: oauthAuth,
  steps: [
    {
      title: "Create API credentials in CrowdStrike",
      items: [
        {
          kind: "markdown",
          content:
            "Log into your [CrowdStrike Falcon console](https://falcon.crowdstrike.com) and navigate to **Support > API Clients and Keys**, then click **Add new API client**. You'll need Falcon Administrator role to create API credentials.",
        },
      ],
    },
    {
      title: "Configure your API client",
      items: [
        {
          kind: "markdown",
          content:
            "Provide a descriptive name and select the appropriate API scopes for your use case. Common scopes include 'Alerts', 'Detections', 'Hosts', and 'Incidents'.",
        },
      ],
    },
    {
      title: "Copy your credentials",
      items: [
        {
          kind: "markdown",
          content:
            "After creating the client, copy the Client ID (32-character hex string) and Client Secret (40-character alphanumeric string). **The secret is only shown once**.",
        },
      ],
    },
    {
      title: "Enter your API credentials",
      items: [
        {
          kind: "input",
          label: "Client ID",
          placeholder: "Paste your Client ID",
          required: true,
          target: { kind: "oauthConnection", field: "clientId" },
        },
        {
          kind: "input",
          label: "Client secret",
          placeholder: "Paste your Client secret",
          required: true,
          target: { kind: "oauthConnection", field: "clientSecret" },
        },
      ],
    },
    {
      title: "Specify your CrowdStrike region",
      items: [
        {
          kind: "select",
          label: "API base URL",
          defaultValue: "api.crowdstrike.com",
          helperText: "Select the base URL for your CrowdStrike region",
          required: true,
          target: { kind: "configuration", field: "baseUrl" },
          options: [
            {
              value: "api.crowdstrike.com",
              label: "US-1 (api.crowdstrike.com)",
            },
            {
              value: "api.us-2.crowdstrike.com",
              label: "US-2 (api.us-2.crowdstrike.com)",
            },
            {
              value: "api.eu-1.crowdstrike.com",
              label: "EU-1 (api.eu-1.crowdstrike.com)",
            },
            {
              value: "api.laggar.gcw.crowdstrike.com",
              label: "US-GOV-1 (api.laggar.gcw.crowdstrike.com)",
            },
            {
              value: "api.us-gov-2.crowdstrike.mil",
              label: "US-GOV-2 (api.us-gov-2.crowdstrike.mil)",
            },
          ],
        },
      ],
    },
  ],
  oauth: {
    grantType: () => "client_credentials",
    tokenEndpoint: ({ baseUrl }) => `https://${baseUrl}/oauth2/token`,
    clientCredentialsFrom: "db",
    tokenEndpointAuthMethod: () => "client_secret_post",
    scopes: () => [],
  },
  connectionTest: {
    protocol: "http",
    request: ({ baseUrl }) => ({
      url: `https://${baseUrl}/sensors/queries/installers/ccid/v1`,
      expectedStatus: [200, 403],
    }),
  },
  publicEnvVars: ({ baseUrl }) => ({
    CROWD_STRIKE_URL: baseUrl ? `https://${baseUrl}` : "",
  }),
});
